import { useMutation, useQuery } from "@apollo/client";
import { CheckIcon } from "@heroicons/react/solid";
import { useMergeLink } from "@mergeapi/react-merge-link";
import githubLogo from "@static/img/github.png";
import linearLogo from "@static/img/linear.png";
import mergeDevLogo from "@static/img/merge-dev.jpg";
import salesforceLogo from "@static/img/salesforce.png";
import slackLogo from "@static/img/slack.png";
import teamsLogo from "@static/img/teams-64.png";
import youtrackLogo from "@static/img/youtrack.svg";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";

import useLabel from "@apps/use-label/use-label";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import AppLink from "@components/link/link";
import { assertNonNull, getAssetUrl } from "@helpers/helpers";

import {
  CreateMergeLinkTokenMutation,
  CreateMergeLinkTokenMutationVariables,
  ExchangeMergePublicTokenMutation,
  ExchangeMergePublicTokenMutationVariables,
  GetMergeDevIntegrationsQuery,
  GetMergeDevIntegrationsQueryVariables,
  GetOrganizationIntegrationsQuery,
  GetOrganizationIntegrationsQueryVariables,
  IntegrationTypes,
} from "../../../types/graphql-schema";
import createMergeLinkTokenMutation from "../graphql/create-merge-link-token";
import exchangeMergePublicTokenMutation from "../graphql/exchange-merge-public-token";
import getMergeDevIntegrationsQuery from "../graphql/get-merge-dev-integrations";
import getOrganizationIntegrations from "../graphql/get-organization-integrations";
import removeGithubFromOrganizationMutation from "../graphql/remove-github-from-organization-mutation";
import removeMergeDevIntegrationMutation from "../graphql/remove-merge-dev-integration";
import removeSlackIntegrationMutation from "../graphql/remove-slack-integration-mutation";
import removeYouTrackIntegrationMutation from "../graphql/remove-youtrack-integration-mutation";
import unlinkOrganizationFromIntegrationMutation from "../graphql/unlink-organization-from-integration-mutation";

export const IntegrationRow = ({
  logoAssetUrl,
  logoAlt,
  text,
  buttons,
  connections,
}: {
  logoAssetUrl: string | URL;
  logoAlt: string;
  text: JSX.Element;
  buttons: JSX.Element;
  connections?: JSX.Element | null;
}) => (
  <li className="flex gap-8 py-6 border-0 border-b border-gray-200">
    <div className="col font-medium mb-4 flex items-start w-28 shrink-0">
      <div className="border rounded flex justify-center items-center h-24 w-28 p-4">
        <img
          src={getAssetUrl(logoAssetUrl)}
          alt={logoAlt}
          className="object-contain h-16"
        />
      </div>
    </div>
    <div className="flex-1">
      {text}
      <div>
        {connections ? (
          <div className="text-xl font-bold">Connections</div>
        ) : null}
        {connections}
      </div>
    </div>
    <div className="w-64 shrink-0 flex flex-col gap-2 items-center">
      {buttons}
    </div>
  </li>
);

const Integrations = ({ organization }: { organization: any }) => {
  const label = useLabel();
  const showNewSlackSettings = waffle.flag_is_active("new-slack-settings");

  const { data } = useQuery<
    GetOrganizationIntegrationsQuery,
    GetOrganizationIntegrationsQueryVariables
  >(getOrganizationIntegrations, {
    variables: { organizationId: organization.id },
  });

  const [unlinkOrganizationFromIntegration] = useMutation(
    unlinkOrganizationFromIntegrationMutation,
  );

  const salesforceIntegrations = data?.organization?.integrations.edges
    .filter(
      (edge) => edge?.node?.integrationType === IntegrationTypes.Salesforce,
    )
    .map((edge) => assertNonNull(edge?.node));
  const linearIntegrations = data?.organization?.integrations.edges
    .filter((edge) => edge?.node?.integrationType === IntegrationTypes.Linear)
    .map((edge) => assertNonNull(edge?.node));
  const [removeSlackIntegration] = useMutation(removeSlackIntegrationMutation);
  const [removeGithub] = useMutation(removeGithubFromOrganizationMutation);
  const handleRemoveSlackIntegration = () => {
    removeSlackIntegration({
      variables: { organizationId: organization.id },
    });
  };
  const handleRemoveGithub = useCallback(() => {
    removeGithub({
      variables: { organizationId: organization.id },

      onCompleted: () => {
        successNotificationVar({ title: "GitHub integration removed" });
      },
    });
  }, [organization.id, removeGithub]);
  const handleRemoveLinear = (id: number) => {
    unlinkOrganizationFromIntegration({
      variables: {
        organizationIntegrationId: id,
      },
    });
  };
  const handleRemoveSalesforceIntegration = (id: number) => {
    unlinkOrganizationFromIntegration({
      variables: {
        organizationIntegrationId: id,
      },
    });
  };

  const [removeYouTrackIntegration] = useMutation(
    removeYouTrackIntegrationMutation,
    {
      refetchQueries: ["getOrganizationSettings"],
      onCompleted: () => {
        successNotificationVar({ title: "YouTrack integration removed" });
      },
    },
  );

  const handleRemoveYouTrackIntegration = () => {
    removeYouTrackIntegration({
      variables: {
        organizationId: organization.id,
      },
    });
  };

  // Merge.dev Magic Link integration
  const [mergeLinkToken, setMergeLinkToken] = useState<string | null>(null);
  const [endUserOriginId, setEndUserOriginId] = useState<string | null>(null);

  const { data: mergeIntegrationsData, refetch: refetchMergeIntegrations } =
    useQuery<
      GetMergeDevIntegrationsQuery,
      GetMergeDevIntegrationsQueryVariables
    >(getMergeDevIntegrationsQuery, {
      variables: { organizationId: organization.id },
    });

  const [createMergeLinkToken, { loading: mergeLinkTokenLoading }] =
    useMutation<
      CreateMergeLinkTokenMutation,
      CreateMergeLinkTokenMutationVariables
    >(createMergeLinkTokenMutation);
  const [exchangeMergePublicToken] = useMutation<
    ExchangeMergePublicTokenMutation,
    ExchangeMergePublicTokenMutationVariables
  >(exchangeMergePublicTokenMutation, {
    onCompleted: () => {
      refetchMergeIntegrations();
    },
  });

  const handleMergeLinkSuccess = useCallback(
    async (publicToken: string) => {
      if (!endUserOriginId) {
        errorNotificationVar({
          title: "Error",
          description: "No endUserOriginId available",
        });
        return;
      }

      try {
        const result = await exchangeMergePublicToken({
          variables: {
            publicToken,
            endUserOriginId,
          },
        });

        if (result.data?.exchangeMergePublicToken?.success) {
          successNotificationVar({
            title: "Success",
            description: "Integration connected successfully",
          });
          setEndUserOriginId(null);
        } else {
          errorNotificationVar({
            title: "Error",
            description:
              result.data?.exchangeMergePublicToken?.error ||
              "Failed to connect integration",
          });
        }
      } catch (error) {
        errorNotificationVar({
          title: "Error",
          description:
            error instanceof Error
              ? error.message
              : "Failed to connect integration",
        });
        throw error;
      }
    },
    [exchangeMergePublicToken, endUserOriginId],
  );

  const { open: openMergeLink, isReady: mergeLinkReady } = useMergeLink({
    linkToken: mergeLinkToken || undefined,
    onSuccess: handleMergeLinkSuccess,
    onExit: () => {
      setMergeLinkToken(null);
    },
  });

  const handleOpenMergeLink = useCallback(async () => {
    try {
      // TODO(TF-784): Temporarily broken until we update we update the frontend
      // to correctly pass in the correct integrationSlug and category
      const result = await createMergeLinkToken({
        variables: {
          integrationSlug: "jira",
          category: "ticketing",
        },
      });

      if (result.data?.createMergeLinkToken?.success) {
        const linkToken = result.data.createMergeLinkToken.linkToken;
        const originId = result.data.createMergeLinkToken.endUserOriginId;
        setMergeLinkToken(linkToken ?? null);
        setEndUserOriginId(originId ?? null);
      } else {
        errorNotificationVar({
          title: "Error",
          description: "Failed to create link token",
        });
      }
    } catch (error) {
      errorNotificationVar({
        title: "Error",
        description: "Failed to create link token",
      });
      throw error;
    }
  }, [createMergeLinkToken]);

  // Auto-open MergeLink when token is set and ready
  useEffect(() => {
    if (mergeLinkToken && mergeLinkReady) {
      openMergeLink();
    }
  }, [mergeLinkToken, mergeLinkReady, openMergeLink]);

  const [removeMergeDevIntegration] = useMutation(
    removeMergeDevIntegrationMutation,
    {
      onCompleted: () => {
        successNotificationVar({ title: "Integration removed" });
        refetchMergeIntegrations();
      },
    },
  );

  const handleRemoveMergeDevIntegration = useCallback(
    (integrationId: number) => {
      removeMergeDevIntegration({
        variables: {
          integrationId,
        },
      });
    },
    [removeMergeDevIntegration],
  );

  return (
    <div>
      <h2 className="text-2xl pb-2 font-medium text-gray-800">Integrations</h2>
      <ul className="bg-white py-4" aria-label="Integration list">
        {showNewSlackSettings ? (
          <IntegrationRow
            logoAlt={"Slack"}
            logoAssetUrl={slackLogo}
            text={
              <div className="space-y-2">
                <p className="text-lg font-semibold text-gray-900">Slack</p>
                <p className="text-gray-600">
                  Integrating Slack with Topicflow lets us extract key messages
                  from public channels, improving context and highlighting
                  important conversations in reviews and AI summaries, without
                  needing to access DMs.
                </p>
              </div>
            }
            buttons={
              <div className="flex flex-col gap-2 items-end min-w-[120px]">
                <Button
                  theme={buttonTheme.lightBlue}
                  size={ButtonSize.large}
                  className="px-4 h-[32px] rounded-lg text-blue-600 bg-blue-50 w-full justify-center"
                  to={
                    organization.hasSlackConnection &&
                    `/settings/organization/${organization.id}/slack`
                  }
                  url={
                    organization.hasSlackConnection
                      ? undefined
                      : `/sso/slack?next=/settings/organization/${organization.id}/integrations&org=${organization.id}`
                  }
                >
                  {organization.hasSlackConnection ? "Configure" : "Connect"}
                </Button>
                <Button
                  theme={buttonTheme.lightGray}
                  size={ButtonSize.large}
                  className="px-4 h-[32px] rounded-lg text-gray-700 bg-gray-50 border-gray-200 w-full justify-center"
                >
                  Learn More
                </Button>
              </div>
            }
          />
        ) : (
          <IntegrationRow
            logoAlt={"Slack"}
            logoAssetUrl={slackLogo}
            text={
              <>
                <p>Connect Slack to your organization.</p>
                <ul className="list-disc ml-4 py-2 italic">
                  <li>Post meeting agendas and summaries to Slack</li>
                  <li>
                    <AppLink
                      to={`/settings/organization/${organization.id}/notifications`}
                      className="hover:underline text-blue-600"
                    >
                      Send notifications
                    </AppLink>{" "}
                    of{" "}
                    {label("recognition", {
                      pluralize: true,
                      capitalize: false,
                    })}{" "}
                    and{" "}
                    {label("goal", {
                      pluralize: false,
                      capitalize: false,
                    })}{" "}
                    check-ins to Slack
                  </li>
                </ul>
              </>
            }
            buttons={
              organization.hasSlackConnection ? (
                <div>
                  <Button
                    theme={buttonTheme.green}
                    rounded
                    className="mx-3 flex"
                    url={`/sso/slack?next=/settings/organization/${organization.id}/integrations&org=${organization.id}`}
                  >
                    <CheckIcon className="text-green-500 h-5 w-5 mr-2" />
                    Connected
                  </Button>
                  <Button
                    theme={buttonTheme.text}
                    onClick={() => {
                      handleRemoveSlackIntegration();
                    }}
                    className="font-light text-xs mt-2 mx-3 py-1 px-2 flex"
                  >
                    Disconnect from Slack
                  </Button>
                </div>
              ) : (
                <Button
                  rounded
                  url={`/sso/slack?next=/settings/organization/${organization.id}/integrations&org=${organization.id}`}
                >
                  Connect to Slack
                </Button>
              )
            }
          />
        )}
        <IntegrationRow
          logoAlt={"Salesforce"}
          logoAssetUrl={salesforceLogo}
          text={
            <>
              <p>Connect your Topicflow and Salesforce accounts.</p>
              <ul className="list-disc ml-4 py-2 italic">
                <li>
                  Preview Salesforce Opportunities in Topicflow meeting notes
                </li>
                <li>Link Salesforce data to Topicflow KPIs</li>
              </ul>
            </>
          }
          buttons={
            salesforceIntegrations && salesforceIntegrations.length > 0 ? (
              <>
                <Button theme={buttonTheme.green} rounded className="flex">
                  <CheckIcon className="text-green-500 h-5 w-5 mr-2" />
                  Connected
                </Button>
              </>
            ) : (
              <Button
                rounded
                url={`/sso/salesforce?next=/settings/organization/${organization.id}/integrations&integration_link_to_org_id=${organization.id}`}
              >
                Connect to Salesforce
              </Button>
            )
          }
          connections={
            salesforceIntegrations?.length ? (
              <ul>
                {salesforceIntegrations?.map((integration) => (
                  <li key={integration.id} className="flex my-2">
                    <span className="font-light text-xs">
                      Connected to{" "}
                      {
                        JSON.parse(integration.socialAuth.extraData)
                          .instance_url
                      }{" "}
                      by {integration.creator.name} on{" "}
                      {moment(integration.created).format("MMM D, YYYY")}
                    </span>
                    <Button
                      theme={buttonTheme.redDanger}
                      onClick={() => {
                        handleRemoveSalesforceIntegration(integration.id);
                      }}
                      className="font-light text-xs py-1 px-2"
                    >
                      Disconnect
                    </Button>
                  </li>
                ))}

                <li className="mt-4">
                  <Button
                    theme={buttonTheme.green}
                    rounded
                    url={`/sso/salesforce?next=/settings/organization/${organization.id}/integrations&integration_link_to_org_id=${organization.id}`}
                  >
                    Add Additional Connection
                  </Button>
                </li>
              </ul>
            ) : null
          }
        />
        <IntegrationRow
          logoAssetUrl={teamsLogo}
          logoAlt="Teams"
          buttons={
            organization.hasTeamsTranscriptConnection ? (
              <Button theme={buttonTheme.green} rounded className="flex">
                <CheckIcon className="text-green-500 h-5 w-5 mr-2" />
                Connected
              </Button>
            ) : (
              <Button
                rounded
                url={`/sso/microsoft?request_transcript_scopes=true&request_calendar_scope=true&next=/msteams/subscribe_to_transcripts?org_id=${organization.id}`}
              >
                Connect to Teams
              </Button>
            )
          }
          text={
            <div>
              <p>
                Connect your organization to Teams to automatically synchronize
                transcripts from your Teams meetings to Topicflow
              </p>
              <p className="mt-8">
                See{" "}
                <a href="https://www.topicflow.com/docs/teams-transcription/">
                  https://www.topicflow.com/docs/teams-transcription/
                </a>{" "}
                for instructions on how to configure this integration.
                Specifically, you must perform some Microsoft application
                configuration before clicking the "Connect to Teams" button
              </p>
            </div>
          }
        />
        <IntegrationRow
          logoAlt={"Github"}
          logoAssetUrl={githubLogo}
          text={
            <>
              <p>Connect your Topicflow and GitHub accounts.</p>
              <ul className="list-disc ml-4 py-2 italic">
                <li>
                  Preview GitHub issues and pull requests in Topicflow meeting
                  notes
                </li>
                <li>
                  Search for GitHub issues and pull requests from the Explorer,
                  and embed lists in meeting notes
                </li>
                <li>Link GitHub metrics to Topicflow KPIs</li>
              </ul>
              <p className="font-bold uppercase py-2 text-gray-400">Note:</p>
              <ul className="list-disc ml-4 text-gray-400">
                <li>
                  Install the Topicflow GitHub app for the organization you want
                  to link with Topicflow
                </li>
              </ul>
            </>
          }
          buttons={
            organization.hasGithubInstallation ? (
              <>
                <Button
                  theme={buttonTheme.green}
                  rounded
                  className="flex"
                  onClick={handleRemoveGithub}
                >
                  <CheckIcon className="text-green-500 h-5 w-5 mr-2" />
                  Connected
                </Button>
              </>
            ) : (
              <Button
                rounded
                url={
                  import.meta.env.DEV
                    ? `https://github.com/apps/topicflow-dev/installations/select_target`
                    : `https://github.com/apps/topicflow/installations/select_target`
                }
              >
                Connect to Github
              </Button>
            )
          }
        />
        <IntegrationRow
          logoAlt={"Linear"}
          logoAssetUrl={linearLogo}
          text={
            <>
              <p>Connect your Topicflow and Linear accounts.</p>
              <ul className="list-disc ml-4 py-2 italic">
                <li>
                  Preview Linear issues and projects in Topicflow meeting notes
                </li>
                <li>
                  Search for Linear issues and projects from the Explorer, and
                  embed lists in meeting notes
                </li>
              </ul>
            </>
          }
          buttons={
            linearIntegrations && linearIntegrations.length > 0 ? (
              <>
                <Button
                  theme={buttonTheme.green}
                  rounded
                  className="flex"
                  onClick={() => {
                    handleRemoveLinear(linearIntegrations[0].id);
                  }}
                >
                  <CheckIcon className="text-green-500 h-5 w-5 mr-2" />
                  Connected
                </Button>
              </>
            ) : (
              <Button
                rounded
                url={`/sso/linear?next=/settings/organization/${organization.id}/integrations&integration_link_to_org_id=${organization.id}`}
              >
                Connect to Linear
              </Button>
            )
          }
        />
        <IntegrationRow
          logoAlt={"YouTrack"}
          logoAssetUrl={youtrackLogo}
          text={
            <>
              <p>Connect your Topicflow and YouTrack accounts.</p>
              <ul className="list-disc ml-4 py-2 italic">
                <li>Sync YouTrack issues to your work history</li>
                <li>Track issue activity and assignments</li>
                <li>
                  Search for YouTrack issues from the Explorer, and embed lists
                  in meeting notes
                </li>
              </ul>
            </>
          }
          buttons={
            organization.hasYoutrackIntegration ? (
              <div>
                <Button theme={buttonTheme.green} rounded className="mx-3 flex">
                  <CheckIcon className="text-green-500 h-5 w-5 mr-2" />
                  Connected
                </Button>
                <Button
                  theme={buttonTheme.text}
                  onClick={handleRemoveYouTrackIntegration}
                  className="font-light text-xs mt-2 mx-3 py-1 px-2 flex"
                >
                  Disconnect from YouTrack
                </Button>
              </div>
            ) : (
              <Button
                rounded
                url={`/sso/youtrack?next=/settings/organization/${organization.id}/integrations&integration_link_to_org_id=${organization.id}`}
              >
                Connect to YouTrack
              </Button>
            )
          }
        />
        {waffle.flag_is_active("merge_dev_enabled") && (
          <IntegrationRow
            logoAlt={"Merge.dev"}
            logoAssetUrl={mergeDevLogo}
            text={
              <>
                <p>
                  Connect your organization to 200+ HR, ATS, Accounting,
                  Ticketing, and CRM platforms through Merge.dev.
                </p>
                <ul className="list-disc ml-4 py-2 italic">
                  <li>
                    Unified API access to multiple third-party integrations
                  </li>
                  <li>
                    Supports HRIS, ATS, Accounting, Ticketing, and CRM
                    categories
                  </li>
                  <li>Secure authentication and data synchronization</li>
                </ul>
              </>
            }
            buttons={
              <Button
                rounded
                onClick={handleOpenMergeLink}
                disabled={mergeLinkTokenLoading}
              >
                {mergeLinkTokenLoading ? "Loading..." : "Add Integration"}
              </Button>
            }
            connections={
              mergeIntegrationsData?.mergeDevIntegrations &&
              mergeIntegrationsData.mergeDevIntegrations.length > 0 ? (
                <ul>
                  {mergeIntegrationsData.mergeDevIntegrations
                    .filter((integration) => integration !== null)
                    .map((integration) => (
                      <li
                        key={integration.id}
                        className="flex items-center gap-2 my-2"
                      >
                        <span className="font-medium">
                          {integration.integrationName || "Unknown"}
                        </span>
                        <span className="text-gray-500 text-sm">
                          ({integration.category})
                        </span>
                        <span className="text-gray-400 text-xs">
                          • Added{" "}
                          {moment(integration.createdAt).format("MMM D, YYYY")}
                        </span>
                        <Button
                          theme={buttonTheme.redDanger}
                          onClick={() =>
                            handleRemoveMergeDevIntegration(integration.id)
                          }
                          className="font-light text-xs py-1 px-2 ml-auto"
                        >
                          Remove
                        </Button>
                      </li>
                    ))}
                </ul>
              ) : null
            }
          />
        )}
      </ul>
    </div>
  );
};

export default Integrations;
